import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import DashboardLayout from "../components/DashboardLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useLang } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";
import { Download, FileImage, FileText, Copy, Check, Plus, Trash2, Lock, Sparkles, LayoutGrid } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import jsPDF from "jspdf";
import { toast } from "sonner";
import { Link } from "react-router-dom";

export default function QRGenerator() {
  const { id } = useParams();
  const { t, lang } = useLang();
  const { user } = useAuth();
  const [restaurant, setRestaurant] = useState(null);
  const [tables, setTables] = useState([]);
  const [activeTableId, setActiveTableId] = useState(null);
  const [newTableLabel, setNewTableLabel] = useState("");
  const [copied, setCopied] = useState(false);
  const qrRef = useRef(null);

  const isPro = user?.plan === "pro" || user?.plan === "premium" || user?.plan === "agency" || user?.role === "admin";

  const tx = (k) => ({
    tablesTitle: { de: "Tisch-spezifische QR-Codes", en: "Table-specific QR codes", tr: "Masa özel QR kodları" },
    tablesDesc: { de: "Erstelle einen QR-Code pro Tisch und tracke Aufrufe getrennt.", en: "Create one QR per table and track scans separately.", tr: "Her masa için QR kod oluşturun ve taramaları ayrı izleyin." },
    addTable: { de: "Tisch hinzufügen", en: "Add table", tr: "Masa ekle" },
    tableLabel: { de: "Tisch-Bezeichnung", en: "Table label", tr: "Masa etiketi" },
    proRequired: { de: "Pro-Plan erforderlich", en: "Pro plan required", tr: "Pro plan gerekli" },
    proRequiredDesc: { de: "Tisch-spezifische QR-Codes sind ein Pro/Agency-Feature.", en: "Table-specific QR codes are a Pro/Agency feature.", tr: "Masa özel QR kodları Pro/Agency özelliğidir." },
    upgrade: { de: "Jetzt upgraden", en: "Upgrade now", tr: "Şimdi yükselt" },
    mainQr: { de: "Haupt-QR-Code", en: "Main QR code", tr: "Ana QR kod" },
  })[k]?.[lang] || "";

  useEffect(() => {
    api.get(`/restaurants/${id}`).then((r) => setRestaurant(r.data));
    api.get(`/restaurants/${id}/tables`).then((r) => setTables(r.data || [])).catch(() => {});
  }, [id]);

  const activeTable = tables.find((tb) => tb.id === activeTableId);
  const baseUrl = restaurant ? `${window.location.origin}/m/${restaurant.slug}` : "";
  const publicUrl = activeTable ? `${baseUrl}?table=${activeTable.id}` : baseUrl;
  const qrLabel = activeTable ? activeTable.label : tx("mainQr");

  const downloadPng = () => {
    const canvas = qrRef.current?.querySelector("canvas");
    if (!canvas) return;
    const url = canvas.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = url;
    a.download = `${restaurant.slug}${activeTable ? "-" + activeTable.label.replace(/\s+/g, "_") : ""}-qr.png`;
    a.click();
    toast.success("PNG");
  };

  const downloadPdf = () => {
    const canvas = qrRef.current?.querySelector("canvas");
    if (!canvas || !restaurant) return;
    const dataUrl = canvas.toDataURL("image/png");
    const pdf = new jsPDF({ unit: "pt", format: "a4" });
    const pageW = pdf.internal.pageSize.getWidth();
    pdf.setFontSize(28); pdf.setFont("helvetica", "bold");
    pdf.text(restaurant.name, pageW / 2, 80, { align: "center" });
    pdf.setFontSize(14); pdf.setFont("helvetica", "normal");
    pdf.text(qrLabel, pageW / 2, 110, { align: "center" });
    const size = 320;
    pdf.addImage(dataUrl, "PNG", (pageW - size) / 2, 150, size, size);
    pdf.setFontSize(10); pdf.setTextColor(120);
    pdf.text(publicUrl, pageW / 2, 510, { align: "center" });
    pdf.text("Powered by qrmenu-pro.com", pageW / 2, 530, { align: "center" });
    pdf.save(`${restaurant.slug}${activeTable ? "-" + activeTable.label.replace(/\s+/g, "_") : ""}-qr.pdf`);
    toast.success("PDF");
  };

  const copyUrl = () => {
    navigator.clipboard.writeText(publicUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const addTable = async (e) => {
    e.preventDefault();
    if (!newTableLabel.trim()) return;
    try {
      const { data } = await api.post(`/restaurants/${id}/tables`, { label: newTableLabel.trim() });
      setTables([...tables, data]);
      setNewTableLabel("");
      toast.success("Added");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const deleteTable = async (tid) => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`/tables/${tid}`);
    setTables(tables.filter((tb) => tb.id !== tid));
    if (activeTableId === tid) setActiveTableId(null);
  };

  return (
    <DashboardLayout restaurantId={id}>
      <div className="space-y-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">{t("dashboard.qrCode")}</div>
            <h1 className="text-3xl font-bold tracking-tight">{t("qr.title")}</h1>
            <p className="text-muted-foreground mt-2 max-w-xl">{t("qr.subtitle")}</p>
          </div>
          <Link
            to={`/dashboard/restaurants/${id}/tables`}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-secondary hover:bg-secondary/80 text-sm font-semibold transition-colors"
            data-testid="link-table-cards"
          >
            <LayoutGrid className="h-4 w-4" />
            {lang === "de" ? "Tisch-Karten" : lang === "tr" ? "Masa Kartları" : "Table Cards"}
          </Link>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          <div className="rounded-3xl border bg-card p-8 grid place-items-center">
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-3">{qrLabel}</div>
            <div ref={qrRef} className="p-6 bg-white rounded-2xl shadow-lg" data-testid="qr-canvas">
              {restaurant && (
                <QRCodeCanvas value={publicUrl} size={260} level="H" includeMargin={true} fgColor="#09090B" bgColor="#FFFFFF" />
              )}
            </div>
          </div>
          <div className="space-y-4">
            <div className="rounded-2xl border bg-card p-5 space-y-3">
              <Label>{t("qr.publicUrl")}</Label>
              <div className="flex gap-2">
                <Input readOnly value={publicUrl} className="rounded-xl font-mono text-xs" data-testid="public-url" />
                <Button variant="outline" onClick={copyUrl} className="rounded-full shrink-0" data-testid="copy-url-btn">
                  {copied ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Button onClick={downloadPng} className="rounded-full h-12 bg-purple-600 hover:bg-purple-700 text-white shadow-lg shadow-purple-500/25" data-testid="download-png">
                <FileImage className="h-4 w-4 mr-2" />
                {t("qr.downloadPng")}
              </Button>
              <Button onClick={downloadPdf} variant="outline" className="rounded-full h-12" data-testid="download-pdf">
                <FileText className="h-4 w-4 mr-2" />
                {t("qr.downloadPdf")}
              </Button>
            </div>
          </div>
        </div>

        {/* Table-specific QR codes (Pro feature) */}
        <div className="rounded-3xl border bg-card p-6">
          <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-bold text-lg">{tx("tablesTitle")}</h3>
                <span className="px-2 py-0.5 rounded-full bg-purple-500/15 text-purple-400 text-[10px] font-bold uppercase tracking-wider border border-purple-500/30">Pro</span>
              </div>
              <p className="text-sm text-muted-foreground mt-1">{tx("tablesDesc")}</p>
            </div>
          </div>

          {!isPro ? (
            <div className="rounded-2xl border border-dashed border-purple-500/30 bg-purple-500/5 p-8 text-center" data-testid="pro-required">
              <Lock className="h-8 w-8 mx-auto text-purple-400 mb-3" />
              <h4 className="font-bold">{tx("proRequired")}</h4>
              <p className="text-sm text-muted-foreground mt-1 mb-4">{tx("proRequiredDesc")}</p>
              <Link to="/pricing">
                <Button className="rounded-full bg-purple-600 hover:bg-purple-700 text-white">
                  <Sparkles className="h-3.5 w-3.5 mr-2" />
                  {tx("upgrade")}
                </Button>
              </Link>
            </div>
          ) : (
            <>
              <form onSubmit={addTable} className="flex gap-2 mb-4">
                <Input
                  placeholder={tx("tableLabel") + ' (z.B. "Tisch 5")'}
                  value={newTableLabel}
                  onChange={(e) => setNewTableLabel(e.target.value)}
                  className="rounded-xl"
                  data-testid="new-table-input"
                />
                <Button type="submit" className="rounded-full bg-purple-600 hover:bg-purple-700 text-white shrink-0" data-testid="add-table-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  {tx("addTable")}
                </Button>
              </form>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                <button
                  onClick={() => setActiveTableId(null)}
                  className={`rounded-2xl border p-4 text-left transition-all ${
                    !activeTableId ? "border-purple-500 bg-purple-500/10" : "hover:border-purple-500/30"
                  }`}
                  data-testid="select-main-qr"
                >
                  <div className="text-xs uppercase tracking-widest font-semibold text-muted-foreground">Main</div>
                  <div className="font-bold mt-1 text-sm">{tx("mainQr")}</div>
                </button>
                {tables.map((tb) => (
                  <div key={tb.id} className="relative" data-testid={`table-card-${tb.id}`}>
                    <button
                      onClick={() => setActiveTableId(tb.id)}
                      className={`w-full rounded-2xl border p-4 text-left transition-all ${
                        activeTableId === tb.id ? "border-purple-500 bg-purple-500/10" : "hover:border-purple-500/30"
                      }`}
                    >
                      <div className="text-xs uppercase tracking-widest font-semibold text-muted-foreground">Table</div>
                      <div className="font-bold mt-1 text-sm truncate">{tb.label}</div>
                      <div className="text-[10px] text-muted-foreground mt-1 font-mono">#{tb.id}</div>
                    </button>
                    <button
                      onClick={() => deleteTable(tb.id)}
                      className="absolute top-2 right-2 w-7 h-7 rounded-full bg-zinc-900 border hover:bg-rose-500/20 hover:text-rose-400 grid place-items-center"
                      data-testid={`delete-table-${tb.id}`}
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
